import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { BsArrowLeftShort } from "react-icons/bs";
import { MdDashboard } from "react-icons/md";
import { IoLogOut } from "react-icons/io5";
import {
  FaNewspaper,
  FaImages,
  FaHandshake,
  FaCog,
  FaHome,
  FaSun,
  FaMoon,
  FaCreditCard,
  FaQuoteLeft,
  FaCalendar,
} from "react-icons/fa";
import { FaUserPen } from "react-icons/fa6";
import { useAsideMenu } from "../../contexts/AsideMenuContext";
import { getSettings } from "../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const safeImg = (src, fallback) => {
  const s = String(src || "");
  if (!s) return fallback;
  return isExternalURL(s) ? s : `${BASE_URL}${s}`;
};

const AsideMenu = () => {
  const { isOpen, toggleMenu } = useAsideMenu();
  const location = useLocation();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [clubInfo, setClubInfo] = useState({ logo: "", nom: "" });
  const [userInfo, setUserInfo] = useState({
    id: 0,
    nom: "",
    prenom: "",
    email: "",
    photoURL: "",
    role_id: 0,
  });

  const menus = useMemo(
    () => [
      { title: "Tableau de bord", path: "/appli/dashboard", icon: <MdDashboard /> },
      { title: "Qui suis-je ?", path: "/appli/aboutme", icon: <FaUserPen /> },
      { title: "Prestations", path: "/appli/prestations", icon: <FaCreditCard /> },
      { title: "Témoignages", path: "/appli/testimonials", icon: <FaQuoteLeft /> },
      { title: "Actualités", path: "/appli/news", icon: <FaNewspaper /> },
      { title: "Albums Photos", path: "/appli/albums-photos", icon: <FaImages /> },
      { title: "Partenaires", path: "/appli/partenaires", icon: <FaHandshake /> },
      { title: "RDV Agenda", path: "/appli/rdv-agenda", icon: <FaCalendar /> },
      { title: "Paramètres", path: "/appli/parametres", icon: <FaCog /> },
      { title: "Abonnement", path: "/appli/abonnement", icon: <FaCreditCard /> },
    ],
    []
  );

  useEffect(() => {
    const init = async () => {
      try {
        const settings = await getSettings();
        const club = settings?.[0] || {};
        setClubInfo({
          logo: club.logo_club || "",
          nom: club.nom_club || "",
        });
      } catch (e) {
        console.error("Erreur settings club:", e);
      }

      const theme = localStorage.getItem("theme");
      const dark = theme === "dark";
      document.documentElement.classList.toggle("dark", dark);
      setIsDarkMode(dark);

      try {
        const raw = localStorage.getItem("user");
        const userData = raw ? JSON.parse(raw) : null;
        if (userData) setUserInfo(userData);
      } catch {
        // ignore parse error
      }
    };

    init();
  }, []);

  const toggleTheme = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const getRoleName = (roleId) => {
    switch (roleId) {
      case 1:
        return "Administrateur";
      case 2:
        return "Bureau";
      case 3:
        return "Enseignant";
      default:
        return "Utilisateur";
    }
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  // Responsive behavior (IMPORTANT):
  // - Mobile (<md): isOpen => drawer visible; !isOpen => drawer hidden (-translate-x-full)
  // - Desktop (md+): isOpen => large (w-72); !isOpen => collapsed (w-20) but still visible
  const asideClass =
    "fixed inset-y-0 left-0 z-[1000] flex flex-col " +
    "border-r border-black/10 dark:border-white/10 " +
    "bg-white/80 dark:bg-slate-950/75 backdrop-blur-xl " +
    "shadow-[0_10px_40px_rgba(0,0,0,0.12)] dark:shadow-none " +
    "transition-all duration-300";

  const asideSize = isOpen
    ? "w-72 translate-x-0"
    : "-translate-x-full w-72 md:translate-x-0 md:w-20";

  const navScroll =
    "flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 pt-6 " +
    "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

  const itemBase =
    "group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 " +
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60";

  const itemText = isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 md:opacity-0 md:-translate-x-2";
  const showText = isOpen ? "md:block" : "md:hidden";

  const iconWrap =
    "h-10 w-10 rounded-2xl flex items-center justify-center " +
    // "bg-black/5 dark:bg-white/10 " +
    "text-slate-700 dark:text-slate-100 " +
    "group-hover:bg-primary/10 group-hover:text-primary transition-colors";

  const activeBar =
    "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 " +
    "before:h-8 before:w-1 before:rounded-full before:bg-primary before:opacity-0";

  // Overlay mobile only
  const showOverlay = isOpen; // sur mobile: ouvre overlay ; desktop: md:hidden
  const overlayClass =
    "fixed inset-0 z-[900] bg-black/50 backdrop-blur-[2px] transition-opacity md:hidden " +
    (showOverlay ? "opacity-100" : "opacity-0 pointer-events-none");

  return (
    <>
      <div className={overlayClass} onClick={toggleMenu} aria-hidden />

      <aside className={`${asideClass} ${asideSize}`} aria-label="Menu latéral">
        {/* Header */}
        <div className="relative px-4 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="shrink-0">
              <img
                src={safeImg(clubInfo.logo, "/images/default_logo.webp")}
                alt="Logo"
                className="h-12 w-12 rounded-2xl object-cover bg-white p-1 border border-black/10 dark:border-white/10"
              />
            </div>

            {/* Nom club */}
            <div className={`min-w-0 transition-all duration-300 ${isOpen ? "opacity-100" : "opacity-0 md:opacity-0"}`}>
              {isOpen && (
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Back-Office
                  </div>
                  <div className="truncate text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary to-fuchsia-500">
                    {clubInfo.nom || "Mon espace"}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Toggle (collapse desktop / drawer mobile) */}
          <button
            type="button"
            onClick={toggleMenu}
            className="
              absolute -right-4 top-7
              h-9 w-9 rounded-2xl
              bg-white dark:bg-slate-900
              border border-black/10 dark:border-white/10
              shadow-sm dark:shadow-none
              flex items-center justify-center
              hover:bg-white/90 dark:hover:bg-slate-900/80
              transition
            "
            aria-label={isOpen ? "Réduire le menu" : "Ouvrir le menu"}
            title={isOpen ? "Réduire" : "Ouvrir"}
          >
            <BsArrowLeftShort className={`text-2xl text-slate-700 dark:text-slate-100 transition-transform ${!isOpen ? "rotate-180 md:rotate-180" : ""}`} />
          </button>
        </div>

        {/* Nav */}
        <div className={navScroll}>
          <nav>
            <ul className="space-y-2">
              {menus.map((menu) => (
                <li key={menu.path}>
                  <NavLink
                    to={menu.path}
                    title={menu.title}
                    className={({ isActive }) =>
                      [
                        itemBase,
                        activeBar,
                        isActive
                          ? "bg-primary/10 text-primary before:opacity-100"
                          : "text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/5",
                      ].join(" ")
                    }
                  >
                    <div className={iconWrap}>
                      <span className="text-xl">{menu.icon}</span>
                    </div>

                    {/* Label */}
                    <span
                      className={[
                        "min-w-0 text-sm font-semibold truncate transition-all duration-300",
                        isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 md:hidden",
                      ].join(" ")}
                    >
                      {menu.title}
                    </span>

                    {/* tiny active dot (desktop collapsed) */}
                    {!isOpen && location.pathname === menu.path && (
                      <span className="ml-auto hidden md:inline-flex h-2 w-2 rounded-full bg-primary" />
                    )}
                  </NavLink>
                </li>
              ))}

              <li className="pt-2">
                <div className="h-px w-full bg-black/10 dark:bg-white/10" />
              </li>

              {/* Accueil */}
              <li>
                <Link
                  to="/"
                  className={`${itemBase} text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/5`}
                  title="Accueil (site)"
                >
                  <div className={iconWrap}>
                    <FaHome className="text-xl" />
                  </div>
                  {isOpen && <span className="text-sm font-semibold">Accueil</span>}
                </Link>
              </li>

              {/* Theme */}
              <li>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className={`${itemBase} w-full text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/5`}
                  title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
                >
                  <div className={iconWrap}>
                    {isDarkMode ? (
                      <FaSun className="text-xl text-yellow-500" />
                    ) : (
                      <FaMoon className="text-xl" />
                    )}
                  </div>
                  {isOpen && (
                    <span className="text-sm font-semibold">
                      {isDarkMode ? "Mode clair" : "Mode sombre"}
                    </span>
                  )}
                </button>
              </li>

              {/* Logout */}
              <li>
                <button
                  type="button"
                  onClick={logout}
                  className={`${itemBase} w-full text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/5`}
                  title="Déconnexion"
                >
                  <div className={iconWrap}>
                    <IoLogOut className="text-xl" />
                  </div>
                  {isOpen && <span className="text-sm font-semibold">Déconnexion</span>}
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* User footer */}
        <div className="border-t border-black/10 dark:border-white/10 p-4">
          <div className="flex items-center gap-3">
            <img
              src={safeImg(userInfo.photoURL, "/images/default_avatar.webp")}
              alt={`${userInfo.prenom} ${userInfo.nom}`}
              className="h-10 w-10 rounded-2xl object-cover border border-black/10 dark:border-white/10"
            />

            {isOpen && (
              <div className="min-w-0">
                <div className="truncate text-sm font-extrabold text-slate-900 dark:text-white">
                  {`${userInfo.prenom || ""} ${userInfo.nom || ""}`.trim() || "Utilisateur"}
                </div>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {getRoleName(userInfo.role_id)}
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default AsideMenu;
